import { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { MessageSquare, Send, Lock } from "lucide-react";

const STATUSES = ["open", "in_progress", "waiting_customer", "resolved", "closed"];
const LABEL = { open: "Open", in_progress: "In Progress", waiting_customer: "Waiting for Customer", resolved: "Resolved", closed: "Closed" };
const CLS = { open: "bg-blue-50 text-blue-700", in_progress: "bg-amber-50 text-amber-700", waiting_customer: "bg-orange-50 text-orange-700", resolved: "bg-emerald-50 text-emerald-700", closed: "bg-slate-100 text-slate-600" };

export default function AdminTickets() {
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState({});
  const [filter, setFilter] = useState("");
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");
  const [internal, setInternal] = useState(false);

  const load = async () => {
    const q = filter ? `?status=${filter}` : "";
    const { data } = await api.get(`/admin/tickets${q}`);
    setTickets(data.tickets); setStats(data.stats);
  };
  useEffect(() => { load(); }, [filter]);

  const openT = async (t) => {
    setActive(t);
    const { data } = await api.get(`/tickets/${t.id}`);
    setMessages(data.messages);
  };
  const sendReply = async () => {
    if (!reply.trim()) return;
    try {
      await api.post(`/tickets/${active.id}/reply`, { message: reply, is_internal: internal });
      setReply(""); setInternal(false);
      const { data } = await api.get(`/tickets/${active.id}`);
      setMessages(data.messages);
      load();
    } catch { toast.error("Reply failed"); }
  };
  const changeStatus = async (s) => {
    try { await api.put(`/admin/tickets/${active.id}/status`, { status: s }); toast.success("Status updated"); setActive({ ...active, status: s }); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <DashboardLayout>
      <div className="mb-4">
        <div className="section-label">Admin</div>
        <h1 className="mt-1 font-display font-bold text-3xl text-[#0A192F]">Support Tickets</h1>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4">
        {STATUSES.map((s) => (
          <button key={s} onClick={() => setFilter(filter === s ? "" : s)} className={`p-3 rounded-lg border text-left ${filter === s ? "border-[#1E3A8A] bg-blue-50" : "border-slate-200 bg-white"}`} data-testid={`admin-ticket-stat-${s}`}>
            <div className="text-xs text-slate-500">{LABEL[s]}</div>
            <div className="text-2xl font-display font-bold text-[#0A192F]">{stats[s] || 0}</div>
          </button>
        ))}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr><th className="p-3 text-left">Ticket ID</th><th className="p-3 text-left">Customer</th><th className="p-3 text-left">Subject</th><th className="p-3 text-left">Category</th><th className="p-3 text-left">Status</th><th className="p-3 text-left">Updated</th><th /></tr>
          </thead>
          <tbody>
            {tickets.map((t) => (
              <tr key={t.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`admin-ticket-row-${t.ticket_id}`}>
                <td className="p-3 font-mono text-xs">{t.ticket_id}</td>
                <td className="p-3"><div>{t.customer_name}</div><div className="text-xs text-slate-500">{t.customer_email}</div></td>
                <td className="p-3">{t.subject}</td>
                <td className="p-3">{t.category}</td>
                <td className="p-3"><span className={`px-2 py-1 rounded text-xs ${CLS[t.status] || "bg-slate-50"}`}>{LABEL[t.status] || t.status}</span></td>
                <td className="p-3 text-xs text-slate-500">{new Date(t.updated_at).toLocaleString()}</td>
                <td className="p-3"><Button size="sm" variant="outline" onClick={() => openT(t)} data-testid={`admin-ticket-open-${t.ticket_id}`}><MessageSquare className="w-3 h-3" /></Button></td>
              </tr>
            ))}
            {tickets.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-slate-500">No tickets</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!active} onOpenChange={(v) => !v && setActive(null)}>
        <DialogContent className="max-w-2xl" data-testid="admin-ticket-conversation">
          <DialogHeader><DialogTitle>{active?.ticket_id} · {active?.subject}</DialogTitle></DialogHeader>
          <div className="text-xs text-slate-500 mb-2">Customer: {active?.customer_name} · {active?.customer_email} {active?.order_number && `· Order ${active.order_number}`}</div>
          <div className="flex gap-2 flex-wrap mb-3">
            {STATUSES.map((s) => (
              <button key={s} onClick={() => changeStatus(s)} className={`text-xs px-3 py-1 rounded border ${active?.status === s ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "bg-white border-slate-200 text-slate-600"}`} data-testid={`admin-ticket-status-${s}`}>{LABEL[s]}</button>
            ))}
          </div>
          <div className="space-y-3 max-h-[45vh] overflow-y-auto pr-1">
            {messages.map((m) => (
              <div key={m.id} className={`p-3 rounded-lg border ${m.is_internal ? "bg-yellow-50 border-yellow-200" : m.author_role === "admin" ? "bg-blue-50 border-blue-100" : "bg-slate-50 border-slate-200"}`}>
                <div className="text-xs text-slate-500 mb-1">
                  {m.is_internal && <Lock className="w-3 h-3 inline mr-1" />}
                  {m.author_name} · <span className="capitalize">{m.author_role}</span> · {new Date(m.created_at).toLocaleString()}
                  {m.is_internal && <span className="ml-2 text-yellow-700 font-medium">INTERNAL NOTE</span>}
                </div>
                <div className="text-sm whitespace-pre-wrap text-slate-800">{m.message}</div>
              </div>
            ))}
          </div>
          <div className="mt-3">
            <label className="flex items-center gap-2 text-xs mb-2"><input type="checkbox" checked={internal} onChange={(e) => setInternal(e.target.checked)} data-testid="admin-ticket-internal-toggle" /> Internal note (customer will NOT see this)</label>
            <div className="flex gap-2">
              <Textarea rows={2} value={reply} onChange={(e) => setReply(e.target.value)} placeholder={internal ? "Internal note…" : "Reply to customer…"} data-testid="admin-ticket-reply-input" />
              <Button onClick={sendReply} className="bg-[#1E3A8A] hover:bg-[#1E40AF]" data-testid="admin-ticket-reply-send"><Send className="w-4 h-4" /></Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
